// src/components/user/tabs/MachinesTab.js
import React, { useState, useEffect, useContext } from "react";
import { Card, Form, Button, Spinner, Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faCog,
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { AuthContext } from "../../../contexts/AuthContext";
import { popup } from "../../common/popup";

const MachinesTab = () => {
  const { currentUser } = useContext(AuthContext);
  // Jika user ditautkan ke sektor/line tertentu, kunci tampilan ke sektor itu
  const lockedSector = currentUser?.sector_id
    ? String(currentUser.sector_id)
    : null;

  const [sectors, setSectors] = useState([]);
  const [machines, setMachines] = useState([]);
  const [selectedSector, setSelectedSector] = useState("");
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState(""); // ✅ TAMBAHKAN INI
  // loading state untuk spinner

  const [showReportModal, setShowReportModal] = useState(false);

  const [selectedMachineId, setSelectedMachineId] = useState(null);

  const [issue, setIssue] = useState("");

  const [symptom, setSymptom] = useState("");

  const [sendingReport, setSendingReport] = useState(false);

  // Fetch sectors & initial machines
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("token");
        const config = { headers: { "x-auth-token": token } };

        // Fetch sectors
        const sectorRes = await axios.get(
          "/api/sectors",
          config,
        );
        const sectorData = sectorRes.data || [];
        setSectors(sectorData);

        // Jika user terkunci ke sektor → tampilkan sektornya saja, selain itu semua
        const initial = lockedSector || "all";
        setSelectedSector(initial);
        await fetchMachines(initial, config);
      } catch (err) {
        console.error("Error mengambil data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockedSector]);

  // Fetch machines untuk sektor tertentu
  const fetchMachines = async (sectorId, config = null) => {
    try {
      if (!config) {
        const token = localStorage.getItem("token");
        config = { headers: { "x-auth-token": token } };
      }

      let res;
      if (sectorId === "all") {
        res = await axios.get(
          "/api/machines",
          config,
        ); // endpoint semua mesin
      } else {
        res = await axios.get(
          `/api/machines/sector/${sectorId}`,
          config,
        );
      }

      setMachines(res.data || []);
    } catch (err) {
      console.error("Error mengambil mesin:", err);
      setMachines([]);
    }
  };

  // Handle ganti sektor
  const handleSectorChange = async (e) => {
    const newSectorId = e.target.value;
    setSelectedSector(newSectorId);
    setLoading(true);
    await fetchMachines(newSectorId);
    setLoading(false);
  };

  // Report issue (USER)
  const handleReportIssue = (machineId) => {
    setSelectedMachineId(machineId);

    setIssue("");

    setSymptom("");

    setShowReportModal(true);
  };

  const submitReport = async () => {
    try {
      if (!symptom.trim()) {
        popup.alert("Gejala masalah wajib diisi");
        return;
      }

      if (!issue.trim()) {
        popup.alert("Uraian masalah wajib diisi");
        return;
      }

      setSendingReport(true);

      const token = localStorage.getItem("token");

      await axios.post(
        `/api/maintenance/report/${selectedMachineId}`,
        {
          symptom,
          issue,
        },
        {
          headers: {
            "x-auth-token": token,
          },
        },
      );

      popup.success("Laporan berhasil dikirim");

      setShowReportModal(false);

      fetchMachines(selectedSector);
    } catch (err) {
      console.error(err);

      popup.error(err.response?.data?.msg || "Gagal melaporkan masalah");
    } finally {
      setSendingReport(false);
    }
  };

  // Helper
  const formatDate = (dateString) => {
    if (!dateString) return "Belum Pernah";

    const date = new Date(dateString);

    return date.toLocaleString("id-ID", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const translateMachineStatus = (status) => {
    switch (status) {
      case "Operational":
        return "Beroperasi";
      case "Need Maintenance":
        return "Butuh Perbaikan";
      case "Under Maintenance":
        return "Sedang Diperbaiki";
      case "Offline":
        return "Tidak Aktif";
      default:
        return status || "Unknown";
    }
  };

  const filteredMachines = machines.filter((m) => {
    const keyword = searchText.toLowerCase();
    return (
      m.name?.toLowerCase().includes(keyword) ||
      m.code?.toLowerCase().includes(keyword)
    );
  });

  return (
    <div className="container-fluid p-2">
      <Card style={{ marginTop: "10px" }}>
        {/* Dropdown sektor */}
        <div className="d-flex align-items-center m-2 gap-2 flex-wrap">
          {lockedSector ? (
            <>
              <span className="me-1 fw-bold">Line:</span>
              <span
                className="badge bg-primary"
                style={{ fontSize: "14px", padding: "8px 12px" }}
              >
                {currentUser?.sector_name ||
                  sectors.find((s) => String(s.id) === lockedSector)?.name ||
                  "-"}
              </span>
            </>
          ) : (
            <>
              <label htmlFor="sectorSelect" className="me-2 fw-bold">
                Pilih Sektor:
              </label>

              <Form.Select
                id="sectorSelect"
                value={selectedSector || "all"}
                onChange={handleSectorChange}
                style={{ width: "220px" }}
                className="form-select-sm"
                disabled={loading || sectors.length === 0}
              >
                <option value="all">All Sector</option>
                {sectors.map((sector) => (
                  <option key={sector.id} value={sector.id}>
                    {sector.name} ({sector.machine_count})
                  </option>
                ))}
              </Form.Select>
            </>
          )}

          <Form.Control
            type="text"
            placeholder="🔍 Cari..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            size="sm"
            style={{ width: "240px" }}
          />

          {loading && <Spinner animation="border" size="sm" />}
        </div>

        <Card.Body style={{ padding: "5px" }}>
          {loading ? (
            <div className="text-center p-5">
              <Spinner animation="border" size="lg" />
            </div>
          ) : !machines || machines.length === 0 ? (
            <div className="text-center p-5">
              <FontAwesomeIcon
                icon={faCog}
                size="3x"
                className="mb-3 text-muted"
              />
              <h4>Tidak ada mesin ditemukan</h4>
              <p>Tidak ada mesin tersedia di sektor yang dipilih.</p>
            </div>
          ) : (
            /* ✅ WRAPPER RESPONSIVE */
            <div
              className="table-wrapper"
              style={{
                width: "100%",
                overflowX: "auto",
                overflowY: "auto",
                maxHeight: "75vh",
              }}
            >
              <table
                className="preview-table"
                style={{
                  minWidth: "800px",
                }}
              >
                <thead className="table-light ">
                  <tr>
                    <th style={{ background: "#1e3a8a", color: "#fff" }}>No</th>
                    <th style={{ background: "#1e3a8a", color: "#fff" }}>
                      Kode
                    </th>
                    <th style={{ background: "#1e3a8a", color: "#fff" }}>
                      Nama
                    </th>
                    <th style={{ background: "#1e3a8a", color: "#fff" }}>
                      Sektor
                    </th>
                    <th style={{ background: "#1e3a8a", color: "#fff" }}>
                      Status
                    </th>
                    <th style={{ background: "#1e3a8a", color: "#fff" }}>
                      Maintenance Terakhir
                    </th>
                    <th style={{ background: "#1e3a8a", color: "#fff" }}>
                      Tindakan
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMachines.map((machine, index) => (
                    <tr key={machine.id}>
                      <td className="text-center">{index + 1}</td>
                      <td className="text-center">{machine.code || "-"}</td>
                      <td className="cell-left">{machine.name}</td>
                      <td>{machine.sector?.name || "-"}</td>
                      <td className="text-center">
                        <span
                          className={`status-badge status-${machine.status
                            ?.toLowerCase()
                            .replace(/\s+/g, "-")}`}
                        >
                          {translateMachineStatus(machine.status)}
                        </span>
                      </td>
                      <td className="text-center">
                        {formatDate(machine.last_maintenance)}
                      </td>
                      <td className="text-center">
                        <Button
                          variant="warning"
                          size="sm"
                          onClick={() => handleReportIssue(machine.id)}
                          disabled={machine.status !== "Operational"}
                          title={
                            machine.status !== "Operational"
                              ? "Mesin sedang bermasalah / maintenance"
                              : "Laporkan kerusakan"
                          }
                        >
                          <FontAwesomeIcon
                            icon={faExclamationTriangle}
                            className="me-1 text"
                          />
                          Laporkan
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card.Body>
      </Card>
      <Modal
        show={showReportModal}
        onHide={() => setShowReportModal(false)}
        centered
        size="md"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon
              icon={faExclamationTriangle}
              className="me-2 text-warning"
            />
            Laporan Kerusakan Mesin
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label className="fw-bold">Gejala Masalah</Form.Label>

            <Form.Control
              type="text"
              placeholder="Contoh: Mesin bergetar, suara kasar, panas berlebih"
              value={symptom}
              onChange={(e) => setSymptom(e.target.value)}
            />
          </Form.Group>

          <Form.Group>
            <Form.Label className="fw-bold">Uraian Masalah</Form.Label>

            <Form.Control
              as="textarea"
              rows={4}
              placeholder="Jelaskan detail kerusakan mesin..."
              value={issue}
              onChange={(e) => setIssue(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowReportModal(false)}>
            Batal
          </Button>

          <Button
            variant="warning"
            onClick={submitReport}
            disabled={sendingReport}
          >
            {sendingReport ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Mengirim...
              </>
            ) : (
              <>
                <FontAwesomeIcon
                  icon={faExclamationTriangle}
                  className="me-2"
                />
                Kirim Laporan
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default MachinesTab;
